import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { initQuote } from 'common/services/QuoteService';
import { initWorkOrder } from 'common/services/WorkOrderService';

import {
    ActivePropertyProfile,
    FolderAttachment,
    Note,
    Property,
} from 'common/types';
import {
    QUOTE_STATUS,
    WORK_ORDER_PRIORITY_TYPE,
    WORK_ORDER_STATUS,
    WORK_ORDER_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { CreationStatus } from 'common/types/creation-status';

import {
    State,
    PreferredContractor,
    PropertyUnit,
    ProfileInfoContractor,
} from 'admin/stores/redux/AdminProperty/interfaces';
import { initialProperty } from 'admin/stores/redux/AdminProperty/constants';

const initialState: State = {
    initialized: false,
    isIncludeParent: false,
    attachedWorkOrder: null,
    attachedQuote: null,
    template: null,
    property: initialProperty,
    propertyUnits: null,
    propertyTree: {},
    selectedProperties: [],
    selectedPropertiesMap: {},
    preFilterSelectedProperties: null,
    profileInfo: {
        contractors: [],
        profiles: [],
        tree: null,
    },
};

export const adminPropertySlice = createSlice({
    name: 'adminProperty',
    initialState,
    reducers: {
        initialize: (state: State, action: PayloadAction<Property>) => {
            state.initialized = true;
            state.property = action.payload;
        },

        initializeProfileInfo: (state: State, action) => {
            const { profiles, contractors, tree } = action.payload;

            state.profileInfo = {
                profiles,
                contractors,
                tree,
            };
        },

        reset: () => {
            return initialState;
        },

        setProperty: (state: State, action: PayloadAction<Property>) => {
            state.property = action.payload;
        },

        addNote: (state: State, action: PayloadAction<Note>) => {
            const notes = [...(state.property.notes ?? [])];

            if (notes?.length > 0) {
                state.property = {
                    ...state.property,
                    notes: notes.concat(action.payload),
                };
            } else {
                notes.push(action.payload);

                state.property = {
                    ...state.property,
                    notes,
                };
            }
        },

        updateNote: (state: State, action: PayloadAction<Note>) => {
            const notes = [...(state.property.notes ?? [])];

            const updateIndex = notes?.findIndex(
                note => note.id === action.payload.id
            );

            notes.splice(updateIndex, 1, action.payload);

            state.property = {
                ...state.property,
                notes,
            };
        },

        addFolderAttachment: (
            state: State,
            action: PayloadAction<FolderAttachment>
        ) => {
            const currentFolderAttachments =
                state?.property?.folder?.folderAttachments || [];

            state.property = {
                ...state.property,
                folder: {
                    ...state.property.folder,
                    folderAttachments: [
                        ...currentFolderAttachments,
                        action.payload,
                    ],
                },
            };
        },

        removeFolderAttachment: (
            state: State,
            action: PayloadAction<number>
        ) => {
            const folderAttachments =
                state?.property?.folder?.folderAttachments || [];

            const filteredAttachments = folderAttachments?.filter(
                attachment => attachment.id !== action.payload
            );

            state.property = {
                ...state.property,
                folder: {
                    ...state.property.folder,
                    folderAttachments: filteredAttachments,
                },
            };
        },

        initializeUnits: (
            state: State,
            action: PayloadAction<PropertyUnit>
        ) => {
            state.propertyUnits = action.payload;
        },

        setPropertyTree: (state: State, action) => {
            state.propertyTree = action.payload;
        },

        setPreFilterSelectedProperties: (state: State) => {
            state.preFilterSelectedProperties = state.selectedProperties;
        },

        setIncludeParentProperty: (
            state: State,
            action: PayloadAction<boolean>
        ) => {
            state.isIncludeParent = action.payload;
        },

        addSelectedProperties: (
            state: State,
            action: PayloadAction<number[] | string[]>
        ) => {
            const properties =
                action.payload?.map(property => property.toString()) || [];

            const combinedProperties = [
                ...new Set([...state.selectedProperties, ...properties]),
            ];

            state.selectedProperties = combinedProperties;
        },

        removeSelectedProperties: (state: State, action) => {
            const properties = [
                ...new Set(
                    action.payload?.map((property: number | string) =>
                        property.toString()
                    )
                ),
            ];

            const removedSelected = state.selectedProperties?.filter(
                selectedProperty => !properties.includes(selectedProperty)
            );

            state.selectedProperties = removedSelected;
        },

        clearSelectedProperties: (state: State) => {
            state.selectedProperties = [];
        },

        updateAttachedWorkOrder: (state: State, action) => {
            state.attachedWorkOrder = action.payload;
        },

        updateAttachedQuote: (state: State, action) => {
            state.attachedQuote = action.payload;
        },

        updateSelectedPropertiesMap: (state: State, action) => {
            state.selectedPropertiesMap = action.payload;
        },

        setTemplate: (state: State, action) => {
            state.template = action.payload;
        },

        initializeBulkQuote: (state: State, action: PayloadAction<number>) => {
            const accountId = action.payload;

            state.template = null;
            state.isIncludeParent = false;
            state.attachedQuote = {
                ...initQuote({ opsJobManager: accountId }),
                creationStatus: CreationStatus.Init,
                statusId: QUOTE_STATUS.CREATING,
                clientId: state?.property?.clientId,
                propertyId: state?.property?.id,
            };
        },

        initializeBulkWorkOrder: (state: State, action) => {
            const { workOrderTypeId = WORK_ORDER_TYPE.WALKTHROUGH, accountId } =
                action.payload;

            state.isIncludeParent = false;
            state.attachedWorkOrder = {
                ...initWorkOrder({
                    opsJobManager: accountId,
                    workOrderTypeId,
                }),
                clientId: state?.property?.clientId,
                propertyId: state?.property?.id,
                lineItems: [],
                statusId: WORK_ORDER_STATUS.CREATING,
                creationStatus: CreationStatus.Init,
                priorityId: WORK_ORDER_PRIORITY_TYPE.ROUTINE,
                sequenceElement: {
                    active: true,
                },
            };
        },

        updateDefaultProfile: (
            state: State,
            action: PayloadAction<ActivePropertyProfile>
        ) => {
            state.property = {
                ...state.property,
                activePropertyProfile: action.payload,
                propertyProfileId: action.payload.id,
            };
        },

        addProfile: (
            state: State,
            action: PayloadAction<ActivePropertyProfile>
        ) => {
            const incomingProfile = action.payload;

            const {
                primaryPreferredContractorId,
                secondaryPreferredContractorId,
                preferredContractor3Id,
                preferredContractor4Id,
            } = incomingProfile;

            const contractors = state?.profileInfo.contractors || [];

            const updatedProfile = {
                ...action.payload,
                primaryPreferredContractor: findPreferredContractor(
                    contractors,
                    primaryPreferredContractorId
                ) as PreferredContractor,
                secondaryPreferredContractor: findPreferredContractor(
                    contractors,
                    secondaryPreferredContractorId
                ) as PreferredContractor,
                preferredContractor3: findPreferredContractor(
                    contractors,
                    preferredContractor3Id
                ),
                preferredContractor4: findPreferredContractor(
                    contractors,
                    preferredContractor4Id
                ),
                property: state.property,
            };

            state.profileInfo.profiles.push(updatedProfile);

            if (!state?.property?.propertyProfileId) {
                state.property = {
                    ...state.property,
                    activePropertyProfile: updatedProfile,
                    propertyProfileId: updatedProfile.id,
                };
            }
        },

        updateProfile: (
            state: State,
            action: PayloadAction<ActivePropertyProfile>
        ) => {
            const incomingProfile = action.payload;

            const {
                primaryPreferredContractorId,
                secondaryPreferredContractorId,
                preferredContractor3Id,
                preferredContractor4Id,
            } = incomingProfile;

            const contractors = state?.profileInfo.contractors || [];

            state.profileInfo.profiles = state.profileInfo?.profiles?.map(
                currentProfile => {
                    if (currentProfile.id === incomingProfile.id) {
                        const updatedProfile = {
                            ...currentProfile,
                            ...incomingProfile,
                            primaryPreferredContractor: findPreferredContractor(
                                contractors,
                                primaryPreferredContractorId
                            ) as PreferredContractor,
                            secondaryPreferredContractor:
                                findPreferredContractor(
                                    contractors,
                                    secondaryPreferredContractorId
                                ) as PreferredContractor,
                            preferredContractor3: findPreferredContractor(
                                contractors,
                                preferredContractor3Id
                            ),
                            preferredContractor4: findPreferredContractor(
                                contractors,
                                preferredContractor4Id
                            ),
                        };
                        if (
                            state.property.propertyProfileId ===
                            updatedProfile.id
                        ) {
                            state.property = {
                                ...state.property,
                                activePropertyProfile: updatedProfile,
                                propertyProfileId: updatedProfile.id,
                            };
                        }

                        return updatedProfile;
                    }
                    return currentProfile;
                }
            );
        },

        removeProfile: (state: State, action: PayloadAction<number>) => {
            const currentProfiles = [...state.profileInfo.profiles]?.filter(
                profile => profile.id !== action.payload
            );

            state.profileInfo.profiles = currentProfiles;
        },
    },
});

const findPreferredContractor = (
    contractors: ProfileInfoContractor[],
    preferredContractorId: number | null
) => {
    if (!preferredContractorId) {
        return null;
    }

    return contractors?.find(
        contractor => contractor?.id === preferredContractorId || null
    );
};

export const {
    initialize,
    initializeProfileInfo,
    reset,
    setProperty,
    addNote,
    updateNote,
    addFolderAttachment,
    removeFolderAttachment,
    initializeUnits,
    setPropertyTree,
    setPreFilterSelectedProperties,
    setIncludeParentProperty,
    addSelectedProperties,
    removeSelectedProperties,
    clearSelectedProperties,
    updateAttachedWorkOrder,
    updateAttachedQuote,
    updateSelectedPropertiesMap,
    setTemplate,
    initializeBulkQuote,
    initializeBulkWorkOrder,
    addProfile,
    removeProfile,
    updateProfile,
    updateDefaultProfile,
} = adminPropertySlice.actions;

export default adminPropertySlice.reducer;
